// Normalizes GovGlance Local News API (api2.govglance.org) /officials/directory
// responses into a flat, render-ready shape.
//
// The endpoint declares no schema (additionalProperties: true), so every field
// read here is defensive. Notable shape differences from api.govglance.org
// /members/:
//   - `name` is one string; there is no first_name / last_name
//   - `photo_url`, not depication.imageUrl
//   - `party` is a full word, and the word varies by upstream source
//   - `district` is a string, and null for statewide seats
//   - officials arrive in buckets (directory.federal, .state, .county, ...),
//     though every row also carries `level`

// No auth: api2 is read-only and public. Do NOT send the api.govglance.org
// bearer token here.
const API2_BASE = 'https://api2.govglance.org';

// ─── Party ────────────────────────────────────────────────────────────────────
// The House clerk feed publishes "Democrat" while OpenStates publishes
// "Democratic" for the same party. Normalize before grouping or sorting, or a
// single state's delegation splits into two clusters.
const PARTY_CANONICAL = {
  republican: 'Republican',
  r: 'Republican',
  gop: 'Republican',
  democrat: 'Democratic',
  democratic: 'Democratic',
  d: 'Democratic',
  independent: 'Independent',
  i: 'Independent',
  libertarian: 'Libertarian',
  green: 'Green',
};

const PARTY_ABBR = {
  Republican: 'R',
  Democratic: 'D',
  Independent: 'I',
  Libertarian: 'L',
  Green: 'G',
};

export const normalizeParty = (party) => {
  if (!party) return '';
  return PARTY_CANONICAL[String(party).trim().toLowerCase()] || String(party).trim();
};

export const partyAbbreviation = (party) => {
  const canonical = normalizeParty(party);
  return PARTY_ABBR[canonical] || canonical.charAt(0).toUpperCase();
};

// ─── Names ────────────────────────────────────────────────────────────────────
// Only a display string is published, so a sort key has to be derived. Suffixes
// are stripped first: "Rudy Yakym III" sorts under Y, not I.
const NAME_SUFFIXES = new Set([
  'jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'v',
  'md', 'm.d.', 'phd', 'ph.d.', 'esq', 'esq.',
]);

export const lastNameOf = (name) => {
  if (!name) return '';
  const parts = String(name).trim().split(/\s+/);
  while (parts.length > 1 && NAME_SUFFIXES.has(parts[parts.length - 1].toLowerCase())) {
    parts.pop();
  }
  // Compound surnames ("Wendy Dant Chesser") collapse to the final token. That
  // is wrong for a handful of people and right for the rest; there is no
  // structured name field to do better with.
  return parts[parts.length - 1] || '';
};

// ─── Identity ─────────────────────────────────────────────────────────────────
// Federal rows put the bioguide ID in external_id ("B001299"). State rows put an
// OpenStates ID in the same field ("ocd-person/bb961150-..."), so the format has
// to be tested rather than trusted.
const BIOGUIDE_PATTERN = /^[A-Z]\d{6}$/;
const FEDERAL_SOURCES = new Set(['us-senate', 'us-house-clerk']);

export const bioGuideIdOf = (row) => {
  const id = row?.external_id || row?.records?.external_id;
  if (!id) return null;
  if (!BIOGUIDE_PATTERN.test(id)) return null;
  if (row?.source && !FEDERAL_SOURCES.has(row.source)) return null;
  return id;
};

// external_id is null on some rows (the Governor, sourced from nga-wikipedia),
// so a composite fallback is required. Duplicate keys previously broke React
// reconciliation on this list -- sorting had no visible effect because the DOM
// kept its original order.
export const officialKey = (row) => {
  const id = row?.external_id || row?.records?.external_id;
  if (id) return id;
  return [
    row?.level || 'unknown',
    row?.chamber || 'unknown',
    row?.state_abbr || '',
    row?.district || 'atlarge',
    row?.name || 'unnamed',
  ].join(':');
};

// ─── Terms ────────────────────────────────────────────────────────────────────
// term_end is ISO ("2027-01-03"). term_start is a formatted string with
// irregular spacing ("January  3, 2025") on federal rows and null on every
// state row, so only the year is worth extracting -- and seniority is unknown
// for state legislators entirely.
const yearFrom = (value) => {
  if (!value) return null;
  const match = String(value).match(/\d{4}/);
  return match ? Number(match[0]) : null;
};

// ─── Row ──────────────────────────────────────────────────────────────────────
export const normalizeOfficial = (row) => {
  if (!row) return null;

  const contact = row.contact || {};
  const actions = Array.isArray(row.actions) ? row.actions : [];

  // Prefer the labelled action over the bare `url` field: some rows point `url`
  // at an aggregator rather than the officeholder's own site (the Indiana
  // Governor row resolves to an NGA Instagram page).
  const officialSite =
    actions.find((action) => action?.label === 'Official website')?.url ||
    contact.website ||
    row.url ||
    null;

  return {
    key: officialKey(row),
    bioGuideId: bioGuideIdOf(row),

    name: row.name || '',
    sortName: lastNameOf(row.name),

    level: row.level || '',
    chamber: row.chamber || '',
    office: row.office || '',
    district: row.district || '',
    stateAbbr: row.state_abbr || '',
    jurisdictionName: row.jurisdiction_name || '',
    geoid: row.geoid || null,

    party: normalizeParty(row.party),
    partyAbbr: partyAbbreviation(row.party),

    photoUrl: row.photo_url || row.records?.photo_url || null,
    website: officialSite,
    phone: contact.phone || row.phone || null,
    email: contact.email || row.email || null,
    officeAddress: (contact.address || row.office_address || '').replace(/\s+/g, ' ').trim(),

    termStartYear: yearFrom(row.term_start),
    termEndYear: yearFrom(row.term_end),

    votesUrl: row.votes_url || row.records?.votes_url || null,
    jobDescription: row.job_description || '',
    sourceUrl: row.source_url || row.records?.source_url || null,
    source: row.source || '',

    social: row.social || {},
    actions,
  };
};

// ─── Payload ──────────────────────────────────────────────────────────────────
const BUCKETS = ['federal', 'state', 'county', 'city', 'other_local'];

// Flattens the bucketed directory into one array. Bucket order is preserved so
// an unsorted render still reads federal -> state -> local.
export const flattenDirectory = (payload) => {
  const directory = payload?.directory || {};
  const flat = [];
  for (const bucket of BUCKETS) {
    const rows = directory[bucket];
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      const official = normalizeOfficial(row);
      if (official) flat.push(official);
    }
  }
  return flat;
};

// The response echoes what it managed to resolve. In state-wide mode
// congressional_district and the GEOIDs come back null -- that absence is the
// signal to prompt for a street address rather than an error.
export const normalizeLocation = (payload) => {
  const location = payload?.location || {};
  return {
    stateAbbr: location.state_abbr || null,
    city: location.city || null,
    county: location.county || null,
    placeGeoid: location.place_geoid || null,
    countyGeoid: location.county_geoid || null,
    congressionalDistrict: location.congressional_district || null,
    stateUpperDistrict: location.state_upper_district || null,
    stateLowerDistrict: location.state_lower_district || null,
    matchedAddress: location.matched_address || null,
    matchType: location.match_type || null,
    isDistrictAccurate: Boolean(location.congressional_district),
    notes: Array.isArray(payload?.coverage?.notes) ? payload.coverage.notes : [],
  };
};

// ─── Fetch ────────────────────────────────────────────────────────────────────
// Precision degrades gracefully: an address resolves exact House and state
// legislative seats; city+state adds local officials without district accuracy;
// state alone returns the delegation and legislature with no city halls.
export const buildDirectoryQuery = ({ address, city, stateAbbr }) => {
  const params = new URLSearchParams();
  if (address) {
    params.set('address', address);
  } else if (city && stateAbbr) {
    params.set('city', city);
    params.set('state', stateAbbr);
  } else if (stateAbbr) {
    params.set('state', stateAbbr);
  } else {
    return null;
  }
  params.set('include_press', 'false');
  return params;
};

export const fetchOfficialsDirectory = async (location, { signal } = {}) => {
  const params = buildDirectoryQuery(location);
  if (!params) throw new Error('No location available for officials lookup');

  const response = await fetch(`${API2_BASE}/officials/directory?${params}`, { signal });
  if (!response.ok) throw new Error('Failed to fetch officials directory');

  const payload = await response.json();
  return {
    officials: flattenDirectory(payload),
    location: normalizeLocation(payload),
    counts: payload?.counts || {},
  };
};

// Resolves a city name to a GEOID. A name alone does not identify a place, so
// `state` and `kind` are both required: eight states have a Springfield, and a
// city commonly shares its name with the surrounding county.
export const resolveJurisdiction = async (city, stateAbbr, kind = 'place', { signal } = {}) => {
  if (!city || !stateAbbr) return null;
  const params = new URLSearchParams({ q: city, state: stateAbbr, kind, limit: '5' });

  const response = await fetch(`${API2_BASE}/jurisdictions?${params}`, { signal });
  if (!response.ok) throw new Error('Failed to resolve jurisdiction');

  const results = await response.json();
  if (!Array.isArray(results) || !results.length) return null;

  const match = results[0];
  return {
    geoid: match.geoid || null,
    name: match.name || city,
    kind: match.kind || kind,
    // 0 means the jurisdiction is known but not yet crawled. Its feed is valid
    // and empty -- render an empty state, not an error.
    articleCount: Number(match.article_count || 0),
  };
};

export default {
  fetchOfficialsDirectory,
  resolveJurisdiction,
  flattenDirectory,
  normalizeOfficial,
  normalizeLocation,
  normalizeParty,
  partyAbbreviation,
  lastNameOf,
  officialKey,
  bioGuideIdOf,
  buildDirectoryQuery,
};